import { useCallback, useEffect, useMemo, useState } from 'react';
import { useVideoThumbnails } from 'hooks/useVideoThumbnails';
import { isVideo } from 'components/uploaded-images/helpers';
import { getMimeTypeFromFileName } from 'utils/helpers/fileCabinetHelpers';
import { resolveFileCabinetIds } from 'utils/services/fileCabinetService';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';

export interface IResolvableMedia {
  url?: string;
  name?: string;
  fileCabinetId?: number;
  options?: { previewUrl?: string } | null;
  transcoded?: { thumbnail?: string };
}

export interface UseResolvedVideoThumbnailsResult {
  getThumbnail: (media: IResolvableMedia) => string | undefined;
  resolvedFromBackend: Map<number, string>;
  generated: Map<string, string>;
}

export const useResolvedVideoThumbnails = (
  media: IResolvableMedia[] | undefined,
  projectId: string | number | undefined,
): UseResolvedVideoThumbnailsResult => {
  const { companyId } = useAppSelector(authSelector);
  const videosNeedingThumbnail = useMemo(
    () =>
      (media || []).filter(
        (m) =>
          !!m.url &&
          isVideo(getMimeTypeFromFileName(m.name || '')) &&
          !m.options?.previewUrl &&
          !m.transcoded?.thumbnail,
      ),
    [media],
  );

  const fileCabinetIdsToResolve = useMemo(
    () =>
      videosNeedingThumbnail
        .map((m) => m.fileCabinetId)
        .filter((id): id is number => typeof id === 'number')
        .sort((a, b) => a - b),
    [videosNeedingThumbnail],
  );
  const fileCabinetIdsKey = fileCabinetIdsToResolve.join(',');

  const [resolvedFromBackend, setResolvedFromBackend] = useState<Map<number, string>>(
    new Map(),
  );

  useEffect(() => {
    if (!projectId || fileCabinetIdsToResolve.length === 0) return;
    let cancelled = false;
    resolveFileCabinetIds(projectId, fileCabinetIdsToResolve)
      .then((response) => {
        if (cancelled) return;
        const next = new Map<number, string>();
        response.items.forEach((item) => {
          if (item.id && item.transcoded?.thumbnail) {
            next.set(item.id, item.transcoded.thumbnail);
          }
        });
        if (next.size > 0) {
          setResolvedFromBackend((prev) => {
            const merged = new Map(prev);
            next.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [fileCabinetIdsKey, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const videoEntriesForRuntime = useMemo(
    () =>
      videosNeedingThumbnail
        .filter(
          (m) => !(m.fileCabinetId && resolvedFromBackend.get(m.fileCabinetId)),
        )
        .map((m) => ({
          id: m.url!,
          url: m.url!,
          fileCabinetId: m.fileCabinetId,
        })),
    [videosNeedingThumbnail, resolvedFromBackend],
  );

  const generated = useVideoThumbnails(
    videoEntriesForRuntime,
    projectId && companyId ? { projectId, companyId } : undefined,
  );

  const getThumbnail = useCallback(
    (m: IResolvableMedia): string | undefined => {
      if (m.options?.previewUrl) return m.options.previewUrl;
      if (m.transcoded?.thumbnail) return m.transcoded.thumbnail;
      if (m.fileCabinetId) {
        const fromBackend = resolvedFromBackend.get(m.fileCabinetId);
        if (fromBackend) return fromBackend;
      }
      if (m.url) return generated.get(m.url);
      return undefined;
    },
    [resolvedFromBackend, generated],
  );

  return { getThumbnail, resolvedFromBackend, generated };
};
